import { addDays, format } from "date-fns";
import { motion } from "framer-motion";
import { AlertCircle, CalendarIcon } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import { Card } from "@/components/card";
import { ListItem } from "@/components/list-item";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { bytesToGi } from "@/features/recordings/utils/bytes-to-gb";
import { queryClient } from "@/lib/react-query";
import { cn } from "@/lib/utils";
import { HttpError, isBadRequest } from "@/types/error";
import { PublicOrganisation } from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  OrganisationLicensingFormData,
  organisationLicensingSchema,
} from "../../organisations/types/organisation-licensing-schema";
import { useLicenses } from "../api/fetch-licenses";
import { useUpdateLicense } from "../api/update-license";

type OrganisationLicenseFormProps = {
  initialData: PublicOrganisation;
};

function transformApiDataToFormData(
  data: PublicOrganisation,
): OrganisationLicensingFormData {
  return {
    licenseId: data.license.licensePlanId,
    licenseEndDate: new Date(data.license.licenseEndDate),
  };
}

export function OrganisationLicenseForm(props: OrganisationLicenseFormProps) {
  const { initialData } = props;
  const form = useForm<OrganisationLicensingFormData>({
    resolver: zodResolver(organisationLicensingSchema),
    defaultValues: transformApiDataToFormData(initialData),
  });

  const {
    data: licenses,
    isLoading: isLoadingLicenses,
    isSuccess: isSuccessLicenses,
  } = useLicenses();

  useEffect(() => {
    form.reset(transformApiDataToFormData(initialData));
  }, [initialData, form]);

  const { mutateAsync: updateLicense } = useUpdateLicense({
    onSuccess: (_, { license }) => {
      queryClient.setQueryData<PublicOrganisation | undefined>(
        ["organisation", initialData.id],
        (oldData: PublicOrganisation | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            license: {
              ...oldData.license,
              licensePlanId: license.licenseId,
              licenseEndDate:
                license.licenseOverrides?.licenseEndDate ??
                oldData.license.licenseEndDate,
            },
          };
        },
      );
    },
  });

  async function handleUpdateLicense(data: OrganisationLicensingFormData) {
    try {
      await updateLicense({
        id: initialData.id,
        license: {
          licenseId: data.licenseId,
          licenseOverrides: {
            licenseEndDate: data.licenseEndDate.toISOString(),
          },
        },
      });
      form.reset(data);
    } catch (error) {
      const httpError = error as HttpError;
      if (isBadRequest(httpError)) {
        form.setError("root", { message: httpError.message.join(",") });
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  }

  return (
    <Form {...form}>
      <Card>
        <form onSubmit={form.handleSubmit(handleUpdateLicense)}>
          <Card.Header title="Details" />
          <Card.Content className="space-y-4">
            <dl className="divide-y divide-border">
              <FormField
                control={form.control}
                name="licenseId"
                render={({ field }) => (
                  <FormItem>
                    <ListItem
                      label={<FormLabel>Pricing Plan</FormLabel>}
                      value={
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                          disabled={
                            isLoadingLicenses || form.formState.isSubmitting
                          }
                        >
                          <FormControl>
                            <SelectTrigger className="w-[240px] capitalize">
                              <SelectValue placeholder="Select a pricing plan..." />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {isSuccessLicenses &&
                              licenses.map((license) => (
                                <SelectItem
                                  key={license.id}
                                  value={license.id}
                                  className="capitalize"
                                >
                                  {license.name.toLowerCase()}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      }
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="licenseEndDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <ListItem
                      label={<FormLabel>License End Date</FormLabel>}
                      value={
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant={"outline"}
                                className={cn(
                                  "w-[240px] pl-3 text-left font-normal",
                                  !field.value && "text-muted-foreground",
                                )}
                                disabled={form.formState.isSubmitting}
                              >
                                {field.value ? (
                                  format(field.value, "PPP")
                                ) : (
                                  <span>Pick a date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={field.onChange}
                              disabled={(date) =>
                                date < addDays(new Date(), -1)
                              }
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      }
                    />

                    <FormMessage />
                  </FormItem>
                )}
              />
              <ListItem
                label="Concurrency"
                value={initialData.license.concurrency}
              />
              <ListItem label="Replay" value={initialData.license.replay} />

              <ListItem
                label="Storage"
                value={`${initialData.license.storageInGB} GB (${bytesToGi(initialData.totalStorageUsedBytes)} GB used)`}
              />
              <ListItem
                label="Permanent Storage"
                value={`${bytesToGi(initialData.permanentStorageUsedBytes)} GB`}
              />
              <ListItem
                label="Storage Duration"
                value={`${initialData.license.storageDurationInDays} days`}
              />
              <ListItem
                label="Support Level"
                value={initialData.license.supportLevel}
              />
              <ListItem
                label="Events"
                value={initialData.license.isGivenEventAccess ? "Yes" : "No"}
              />
            </dl>

            {form.formState.errors.root && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Unexpected error</AlertTitle>
                <AlertDescription>
                  {form.formState.errors.root.message}
                </AlertDescription>
              </Alert>
            )}
          </Card.Content>
          <motion.div
            className="overflow-hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={
              form.formState.isDirty
                ? { opacity: 1, height: "auto" } // 224px is equivalent to h-56
                : { opacity: 0, height: 0 }
            }
            transition={{ duration: 0.3 }}
          >
            <Card.Footer className="flex gap-4 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  form.reset(transformApiDataToFormData(initialData))
                }
              >
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={form.formState.isSubmitting}
                isAnimated
              >
                Update
              </Button>
            </Card.Footer>
          </motion.div>
        </form>
      </Card>
    </Form>
  );
}
